import React, { useCallback, useEffect, useRef, useState } from 'react';

import { motion } from 'framer-motion';
import QuoteStore from '@/stores/QuoteStore';
import { observer } from 'mobx-react';

// import { UploadMultipleFiles } from '@/shared';
import {
    ITARContactSalesForm,
    ITARUploadRequirement,
    MatchPartFilesTool,
    UploadMultipleFiles,
} from '@partsbadger/common-features';
import type { ITARUploadAnswer } from '@partsbadger/common-features';
import { CustomModal } from '@partsbadger/ui-library';
import { Footer } from '@/components/GetQuote/Welcome/Footer';
import {
    QuoteLineItemProps as QuoteLineItemPropsFromTypes,
    QuoteProps as QuotePropsFromTypes,
} from '@partsbadger/types';
import { PartsManager } from '../../../components/GetQuote/PartsManager';
import { isAnyLineItemAnalyzing } from '../../../components/GetQuote/lineItemAnalysis';
import { useQueryParameters, allowed_2d, allowed_3d, post } from '@partsbadger/utils';
import SocketConnection from '../../../components/GetQuote/SocketConnection';
import UserStore, { IGuestData } from '../../../stores/UserStore';
import { Alert, Form, message, notification, Spin, Tooltip } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { AdditionalRequirementStore, CoatingStore, FinishStore, MaterialStore } from '../../../stores';
import { ErrorRender, EmbeddedAttachmentsModal } from '@partsbadger/library';
import QuoteSideBar from '../../../components/GetQuote/QuoteSideBar';
import { GAEvent, PageView } from '../../../utils/tracking';
import Feedback from '../../../components/Feedback';
import { AuthModal, LoginForm, RegisterForm } from '../../../components/RapidQuote';
import {
    useFileUploadFlow,
    usePrefetchQuoteOptions,
    useQuoteBootstrap,
    useQuoteLineItemHandlers,
    useQuotePartsHandlers,
    useValidationErrorsHandlers,
} from './hooks';
import { WelcomeHeader } from '@/components/GetQuote/Welcome/Header';
import { AxiosError } from 'axios';
import LoginOrRegisterModal from '../../../components/GetQuote/LoginOrRegisterModal';
import UIStore from '@/stores/UIStore';
import AddingPartsCard from './AddingPartsCard';
import BackgroundUploadTray from './BackgroundUploadTray';
interface IProps {
    match: {
        params: {
            id: string;
        };
    };
}
interface IRouteParams {
    id: string;
}

const WelcomeToGetQuote: React.FC<IProps> = () => {
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [quoteId, setQuoteId] = useState<number | null>(null);
    const [showFeedbackModal, setShowFeedbackModal] = useState<boolean>(false);
    const [errorPartId, setErrorPartId] = useState<number | null>(null);
    const [errorStepId, setErrorStepId] = useState<number | null>(null);
    const [scrollTick, setScrollTick] = useState(0);
    const [authModalVisible, setAuthModalVisible] = useState(false);
    const [authModalForm, setAuthModalForm] = useState<'login' | 'register'>('register');
    const [requestModalLoggedIn, setRequestModalLoggedIn] = useState<boolean>(false);
    const [pendingCustomByPartStep, setPendingCustomByPartStep] = useState<Record<string, boolean>>({});
    const [itarUploadAnswer, setITARUploadAnswer] = useState<ITARUploadAnswer>(null);
    const [showITARRequiredError, setShowITARRequiredError] = useState(false);
    const [itarFocusSignal, setITARFocusSignal] = useState(0);
    const isSubmittingRef = useRef(false);

    const hasPendingCustomOption = Object.values(pendingCustomByPartStep).some(Boolean);

    const handlePendingCustomChange = useCallback((partId: number, stepIndex: number, isPending: boolean) => {
        const key = `${partId}:${stepIndex}`;
        setPendingCustomByPartStep(prev => {
            if (prev[key] === isPending) return prev;
            return { ...prev, [key]: isPending };
        });
    }, []);

    useEffect(() => {
        if (!openModal) {
            isSubmittingRef.current = false;
        }
    }, [openModal]);

    const userToken = UserStore.token ? UserStore.token : '';
    const quote = QuoteStore.quote;
    const zipCode = QuoteStore.getZipCode;

    const lineItems = QuoteStore.getQuoteLineItems ?? QuoteStore.quote?.line_items ?? [];

    useEffect(() => {
        const validPartIds = new Set(lineItems.map(item => item.id));
        setPendingCustomByPartStep(prev => {
            const next = Object.fromEntries(
                Object.entries(prev).filter(([key]) => validPartIds.has(Number(key.split(':')[0])))
            );
            return Object.keys(next).length === Object.keys(prev).length ? prev : next;
        });
    }, [lineItems]);

    useEffect(() => {
        if (!quote) {
            setITARUploadAnswer(null);
            return;
        }
        if (quote.itar_notice_answer !== undefined) {
            setITARUploadAnswer(quote.itar_notice_answer ?? null);
        }
    }, [quote, quote?.itar_notice_answer]);

    // This paramenter will be availaable if the users is accesing from<Route /my-account/quotes/:id/continue/
    const { id } = useParams<IRouteParams>();

    const history = useHistory();
    const searchParams = useQueryParameters();
    const location = useLocation();
    const isRapidQuoteFlow = location.pathname.startsWith('/rapid-quote');
    const isRapidQuoteStartRoute = location.pathname.replace(/\/+$/, '') === '/rapid-quote/start';
    const hasLineItems = quote && quote?.line_items?.length > 0;
    const shouldShowUploadPage = !quote || quote?.line_items?.length == 0;
    const isRapidQuotePublicShell = isRapidQuoteStartRoute && shouldShowUploadPage;
    const itarUploadAllowed = !shouldShowUploadPage || itarUploadAnswer === false;
    const workspaceSurface = QuoteStore.quoteLoading ? 'loading' : shouldShowUploadPage ? 'empty' : 'configured';
    const isBootstrappingQuote = QuoteStore.quoteLoading && !quote;
    const [rapidQuoteStartReady, setRapidQuoteStartReady] = useState(false);
    const isRapidQuotePublicReady = isRapidQuotePublicShell && !isBootstrappingQuote && rapidQuoteStartReady;

    useEffect(() => {
        if (isRapidQuotePublicShell && !isBootstrappingQuote) {
            setRapidQuoteStartReady(true);
        }
    }, [isBootstrappingQuote, isRapidQuotePublicShell]);

    const [loginForm] = Form.useForm();

    const goToNewQuote = () => {
        history.push('/my-account/quotes/new-quote/');
    };

    useQuoteBootstrap({
        id,
        isRapidQuoteFlow,
        goToNewQuote,
    });

    //
    // /**
    //  * Hide the footer when accessing the quote page to provide more room for the parts manager
    //  * and avoid distractions, since this is the main focus of the page. We will show it again
    //  * when the component unmounts.
    //  */
    // useEffect(() => {
    //     const footer = document.getElementsByClassName('footer-container')[0];
    //
    //     if (footer) {
    //             footer.classList.add('hidden');
    //     }
    //
    //     return () => {
    //         if (footer) {
    //             footer.classList.remove('hidden');
    //         }
    //     }
    // }, []);

    usePrefetchQuoteOptions();

    const shouldOpenMultiplePartsModal = (fileParts: any[] = []) => {
        return fileParts.length > 1;
    };

    const notifyError = (error: AxiosError, message = 'Error') => {
        if (error) {
            notification.error({
                message,
                description: <ErrorRender error={error} />,
            });
        }
    };

    const { handleUploadFile } = useFileUploadFlow({
        setAuthModalVisible,
        setOpenModal,
        itarUploadAllowed,
        showITARRequiredError: () => {
            setShowITARRequiredError(true);
            setITARFocusSignal(signal => signal + 1);
        },
    });

    const handleITARUploadAnswerChange = (answer: ITARUploadAnswer) => {
        setITARUploadAnswer(answer);
        setShowITARRequiredError(false);
        if (quote) {
            handleUpdateQuoteFields({ itar_notice_answer: answer });
        }
    };

    const handleITARContactFormSubmit = async (msg: string) => {
        if (!quote?.uid) throw new Error('No active quote. Please try again.');
        await post(`/customer/quote/${quote.uid}/itar-contact/`, { message: msg });
    };

    const handleBlockedITARUploadIntent = () => {
        if (itarUploadAllowed) return;

        setShowITARRequiredError(true);
        setITARFocusSignal(signal => signal + 1);
    };

    const { handleQuoteValidationErrors } = useValidationErrorsHandlers({
        setLoading,
        setErrorPartId,
        setErrorStepId,
        setScrollTick,
    });
    const { submitParts, removePart } = useQuotePartsHandlers({
        openModal,
        authModalVisible,
        setOpenModal,
    });

    const {
        updateLineItem,
        duplicateItem,
        removeMasterProduct,
        removeVariation,
        updateItemPrice,
        onLineItemUpdated,
    } = useQuoteLineItemHandlers({
        setLoading,
        setErrorPartId,
        setErrorStepId,
        setScrollTick,
        notifyError,
        zipCode,
    });

    // Fallback: poll quote when any part is still "Analyzing geometry" or "Analyzing 2D File" (in case WebSocket never delivers)
    const hasAnyAnalyzing = isAnyLineItemAnalyzing(lineItems);
    useEffect(() => {
        const quoteUid = QuoteStore.getQuoteUid;
        if (!hasAnyAnalyzing || !quoteUid) return;
        const interval = setInterval(() => {
            const active = document.activeElement;
            if (active?.tagName && ['INPUT', 'TEXTAREA', 'SELECT'].includes(active.tagName)) return;
            QuoteStore.getQuoteByUid(quoteUid, {
                updateLineItems: true,
                useSummaryLoading: false,
            });
        }, 30000); // 30s
        return () => clearInterval(interval);
    }, [hasAnyAnalyzing]);

    const handleUpdateQuoteFields = async (payload: Record<string, unknown>) => {
        await QuoteStore.updateQuoteFields(payload)
            .then(() => {
                // notification.success({
                //     key: 'quote-updated',
                //     message: 'Success...',
                //     description: 'Quote updated successfully....',
                //     duration: 2,
                //     placement: 'topRight',
                // });
                // if (QuoteStore.getQuoteUid) {
                //     QuoteStore.getQuoteByUid(QuoteStore.getQuoteUid);
                // }
            })
            .catch(error =>
                notification.error({
                    key: 'quote-updated',
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                    maxCount: 1,
                    placement: 'topRight',
                })
            );
    };

    const handleRapidQuoteSubmit = async (user_info: IGuestData) => {
        setLoading(true);

        try {
            const getParam = (param: string) => searchParams.get(param) || null;

            const utmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');

            const utm_campaign = utmParams.utm_campaign;
            const utm_medium = utmParams.utm_medium;
            const utm_source = utmParams.utm_source;
            const first_source = utmParams.first_source;

            const li_fat_id = getParam('li_fat_id');
            const gclid = utmParams.gclid || getParam('gclid');
            const fbclid = getParam('fbclid');

            const email = user_info.email.toLowerCase();

            const emailExists = await UserStore.checkEmailExists(email);
            if (emailExists) {
                setAuthModalForm('login');
                notification.info({
                    key: 'rapid-quote-account-exists',
                    message: 'Account already exists',
                    description:
                        'To continue with your quote, please log in to your existing account or recover your password.',
                });
                setLoading(false);
                return;
            }

            const payload = {
                ...user_info,
                email,
                utm_campaign,
                utm_medium,
                utm_source,
                first_source,
                li_fat_id,
                gclid,
                fbclid,
                campaign_tracking: utmParams,
            };

            GAEvent('Form', 'Rapid Quote Started');

            UserStore.setGuestData(user_info);

            await QuoteStore.getRapidQuote(payload);

            if (shouldOpenMultiplePartsModal(QuoteStore.fileParts)) {
                setOpenModal(true);
            }
            setAuthModalVisible(false);
        } catch (error) {
            message.error('Error, please try again');
            window.localStorage.removeItem('quote_token');
        }

        setLoading(false);
    };

    const buildCheckoutUrl = (orderId: number | string) => `/my-account/orders/checkout/${orderId}/`;

    const handleSubmitRFQ = async (values: { [key: string]: any }) => {
        setLoading(true);
        const markAsCompleted = values?.markAsCompleted;

        if (QuoteStore.errorFields?.length === 0) {
            if (!quote?.line_items?.length) {
                message.error('Please select your parts files');
                return null;
            }
            let file_2d_required = '';
            for (const item of QuoteStore.quote?.line_items || []) {
                if (item?.product?.material.includes('Customer')) {
                    values.customer_supplied_material = true;
                }

                if (item?.product?.material.includes('Review 2D') && !item?.product?.has_file_2d) {
                    file_2d_required = item.name;
                }

                item?.product?.finishes.forEach(finish => {
                    if (finish === 'Review 2D' && !item?.product?.has_file_2d) {
                        file_2d_required = item.name;
                    }
                });

                item?.product?.coatings.forEach(coating => {
                    if (coating === 'Review 2D' && !item?.product?.has_file_2d) {
                        file_2d_required = item.name;
                    }
                });

                if (quote?.contact?.is_hobbyist && !item?.product?.has_file_3d) {
                    setLoading(false);
                    notification.error({
                        key: 'form-errors',
                        duration: 8,
                        message: 'Missing Required 3D File',
                        description: `A 3D file is required for '${item.name}'. Please upload the necessary file to continue.`,
                        placement: 'topRight',
                        maxCount: 1,
                    });
                    return;
                }
            }

            if (file_2d_required) {
                setLoading(false);
                notification.error({
                    key: 'form-errors',
                    duration: 8,
                    message: 'Missing Required 2D File',
                    description: `The selected options for '${file_2d_required}' require a 2D drawing. Please upload a 2D file to continue.`,
                    placement: 'topRight',
                    maxCount: 1,
                });
                return;
            }

            const utmParams = JSON.parse(localStorage.getItem('utm_params') || '{}');

            const payload = {
                ...values,
                other_notes: values.other_notes,
                customer_statement: values.customer_statement,
                requote_changes: values.requote_changes,
                acquisition_source: 'Portal',
                source: 'One True Form - Customer',
                create_3d_files: values.create_3d_files || false,
                create_2d_files: values.create_2d_files || false,
                additional_files: values.additional_files?.fileList?.map((f: any) => f.response?.id ?? Number(f.uid)),
                is_a_referral: values.is_a_referral,
                domestic_quoting: values.domestic_quoting,
                customer_supplied_material: values.customer_supplied_material,
                campaign_tracking: utmParams,
                only_complete: markAsCompleted,
            };

            // if (values.additional_requirements) {
            //     payload['custom_requirements'] = values.additional_requirements;
            // }

            QuoteStore.sendToReview(payload)
                .then(data => {
                    setLoading(false);

                    if (!(data.quote_stage === 'Completed')) {
                        if (!markAsCompleted) {
                            if (data?.sales_order) {
                                if (isRapidQuoteFlow) {
                                    // Redirect to the checkout page
                                    window.location.replace(buildCheckoutUrl(data.sales_order));
                                } else {
                                    // Push to the checkout page
                                    history.push(buildCheckoutUrl(data.sales_order));
                                }
                                return;
                            }
                        }

                        const date = new Date();

                        (window as any).dataLayer?.push({
                            event: 'submitted-rfq',
                            url: window.location.toString(),
                            time: date.getTime(),
                        });
                    }

                    if (isRapidQuoteFlow) {
                        PageView('/rapid-quote/success/');
                        GAEvent('Form', 'Rapid Quote Submitted');

                        const goToUrl = data.isNew ? '/register/' : '/login/';

                        history.push(`${goToUrl}?next=/my-account/quotes`, {
                            isNew: data.isNew,
                            first_name: quote?.first_name,
                            last_name: quote?.last_name,
                            email: quote?.email,
                            company: quote?.company ? quote?.company : quote?.account,
                            quoteSubmitted: true,
                        });
                        QuoteStore.cleanRapidQuoteData();

                        return;
                    }

                    GAEvent('Form', 'RFQ Submitted');

                    PageView('/rapid-quote/success/');
                    setShowFeedbackModal(true);

                    if (data.quote_id) {
                        setQuoteId(data.quote_id);
                    }

                    const args = {
                        message: 'Your Quote was sent!',
                        description:
                            "Thank you for using PartsBadger's Quote submission form. Your quote will be updated shortly!",
                        duration: 20,
                    };
                    notification.success(args);
                })
                .catch(err => {
                    message.error('Error', err);
                    setLoading(false);
                });

            return true;
        }

        notification.error({
            message: 'Action Required',
            description: 'Please review the highlighted parts and complete all required fields',
            key: 'form-errors',
        });

        handleQuoteValidationErrors();
    };

    const redirectToQuotes = () => {
        if (quoteId) {
            return history.push(`/my-account/quotes/${quoteId}`);
        }
        history.push('/my-account/quotes');
    };

    const totalParts = QuoteStore.line_items.length;

    const submitRapidQuoteLogin = async (values: any) => {
        setLoading(true);
        await UserStore.login(values.email, values.password1)
            .then(() => {
                history.push('/my-account/quotes/new-quote/');
            })
            .catch(error => {
                notification.error({
                    message: 'Error',
                    description: <ErrorRender error={error} />,
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div
            data-testid={isRapidQuotePublicShell ? 'page-customer-rapid-quote-public' : 'page-quote-workspace'}
            data-ready={isRapidQuotePublicShell ? (isRapidQuotePublicReady ? 'true' : 'false') : undefined}
            data-loading={isRapidQuotePublicShell ? (isBootstrappingQuote ? 'true' : 'false') : undefined}
            data-workspace-surface={workspaceSurface}
            className={`one-true-form-container max-w-[1600px] mx-auto px-4 sm:px-6 xl:px-8 grid grid-cols-1 ${
                hasLineItems ? 'xl:grid-cols-3' : ''
            } gap-8`}
            style={{
                flex: 1,
                display: 'grid',
                marginTop: '1rem',
                marginBottom: '1rem',
                // Safari (WebKit) needs a definite `height` here, not just `minHeight`, to reliably
                // resolve the `height: '100%'` on `left-content` and the grid stretch on `right-content`.
                // Without it, the inner scroll region below never gets bounded and Safari clips the
                // bottom of the parts list instead of scrolling (PTB-6298).
                ...(hasLineItems ? { height: 'calc(100vh - 2rem)', minHeight: 0 } : { minHeight: 0 }),
            }}
            // spinning={QuoteStore.quoteLoading} indicator={<LoadingOutlined style={{ fontSize: 34 }} />}
        >
            <>
                {/* Main Content */}
                <div
                    className={hasLineItems ? 'left-content lg:col-span-2' : 'left-content'}
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: hasLineItems ? 0 : undefined,
                        ...(hasLineItems && !UIStore.isMobileViewport ? { height: '100%' } : {}),
                    }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            // overflowY: UIStore.isConstrainedViewport ? 'visible' : 'auto',
                        }}
                    >
                        {/* Welcome/Getting Started Message - Show BEFORE PartsManager when no parts */}
                        {quote?.type === 'Requote' && (
                            <Alert
                                type="info"
                                showIcon
                                message="This is a Requote"
                                description="Adjust your part requirements and submit to have a Design Engineer review the changes."
                                className="mb-4"
                            />
                        )}

                        {isBootstrappingQuote ? (
                            <div
                                data-testid={
                                    isRapidQuotePublicShell ? 'customer-rapid-quote-public-skeleton' : undefined
                                }
                                data-loading={isRapidQuotePublicShell ? 'true' : undefined}
                                className="flex items-center justify-center py-24"
                            >
                                <Spin size="large" indicator={<LoadingOutlined style={{ fontSize: 34 }} />} />
                            </div>
                        ) : (
                            <>
                                {!shouldShowUploadPage ? (
                                    <div
                                        data-testid="parts-manager-header"
                                        className="parts-manager-title sticky top-0 z-30 px-6 py-3 flex items-center justify-between rounded-t-xl"
                                        style={{
                                            backgroundColor: '#000',
                                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                                        }}
                                    >
                                        <div className="flex items-center gap-2">
                                            <h2 className="text-xl font-bold text-white">Parts Manager</h2>

                                            <span data-testid="parts-count" className="text-md text-slate-300">
                                                ({totalParts} part{totalParts !== 1 ? 's' : ''})
                                            </span>
                                        </div>

                                        <div style={{}}></div>

                                        <div data-testid="quote-primary-upload">
                                            <Tooltip title="Upload 2D or 3D  file">
                                                <UploadMultipleFiles
                                                    disabled={false}
                                                    isUploading={
                                                        QuoteStore.fileParts.find(filePart => filePart.uploading)
                                                            ? true
                                                            : false
                                                    }
                                                    hasItems={hasLineItems ? true : false}
                                                    totalParts={
                                                        QuoteStore.line_items.length ? QuoteStore.line_items.length : 0
                                                    }
                                                    onUploadFile={part => handleUploadFile(part)}
                                                    as="button"
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                ) : null}

                                <div
                                    className={`parts-manager-content ${
                                        shouldShowUploadPage &&
                                        'bg-white rounded-2xl shadow-xl p-10 mb-6 border-2 border-slate-200 max-w-5xl mx-auto'
                                    }`}
                                    style={{
                                        flex: 1,
                                        minHeight: 0,
                                        display: 'flex',
                                        flexDirection: 'column',
                                    }}
                                >
                                    {shouldShowUploadPage && <WelcomeHeader isRapidQuoteFlow={isRapidQuoteFlow} />}

                                    {shouldShowUploadPage && (
                                        <ITARUploadRequirement
                                            className="mb-4"
                                            value={itarUploadAnswer}
                                            onChange={handleITARUploadAnswerChange}
                                            showRequiredError={showITARRequiredError}
                                            focusSignal={itarFocusSignal}
                                        />
                                    )}

                                    {/*Left Content*/}

                                    {/* Parts Manager + Drop files. On mobile no inner scroll—page scroll only. */}
                                    {quote && (
                                        <div
                                            style={{
                                                ...(UIStore.isMobileViewport
                                                    ? { flex: 'none' as const, overflowY: 'visible' as const }
                                                    : { flex: 1, minHeight: 0, overflowY: 'auto' as const }),
                                                marginBottom: `${!shouldShowUploadPage ? '2rem' : '0'}`,
                                            }}
                                        >
                                            <AddingPartsCard />
                                            <PartsManager
                                                lineItemsLength={QuoteStore.line_items.length}
                                                activePart={QuoteStore.getExpandedPartId}
                                                setActivePart={QuoteStore.setExpandedPartId}
                                                onUpdateLineItem={data => updateLineItem(data)}
                                                materialsList={MaterialStore.materials}
                                                topMaterials={MaterialStore.topMaterials}
                                                coatingsList={CoatingStore.coatings}
                                                topCoatings={CoatingStore.topCoatings}
                                                finishesList={FinishStore.finishes}
                                                loadingPartId={QuoteStore.loadingLineItemPartId || null}
                                                onUpdateItemPrice={(itemId, id) => updateItemPrice(itemId, id)}
                                                onRemoveMasterProduct={id => removeMasterProduct(id)}
                                                onDuplicateItem={duplicateItem}
                                                onRemoveVariation={removeVariation}
                                                errorPartId={errorPartId}
                                                errorStepId={errorStepId}
                                                scrollTick={scrollTick}
                                                quoteToken={QuoteStore?.getQuoteUid ?? quote?.uid}
                                                isRapidQuote={!!QuoteStore.rapidQuoteUid}
                                                sortedMasterProducts={
                                                    (QuoteStore.sortedMasterProducts as unknown) as QuoteLineItemPropsFromTypes[][]
                                                }
                                                isHobbyist={quote?.contact?.is_hobbyist || false}
                                                isRequote={quote?.type === 'Requote'}
                                                onPendingCustomChange={handlePendingCustomChange}
                                            />
                                            {itarUploadAnswer === true ? (
                                                <div className="mt-4">
                                                    <ITARContactSalesForm onSubmit={handleITARContactFormSubmit} />
                                                </div>
                                            ) : (
                                                <div
                                                    data-testid="quote-add-part-upload"
                                                    className="add-part-files-container mt-4"
                                                    onClickCapture={handleBlockedITARUploadIntent}
                                                >
                                                    <UploadMultipleFiles
                                                        disabled={!itarUploadAllowed}
                                                        isUploading={
                                                            QuoteStore.fileParts.find(filePart => filePart.uploading)
                                                                ? true
                                                                : false
                                                        }
                                                        hasItems={hasLineItems ? true : false}
                                                        totalParts={
                                                            QuoteStore.line_items.length
                                                                ? QuoteStore.line_items.length
                                                                : 0
                                                        }
                                                        onUploadFile={part => handleUploadFile(part)}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    {/* When no quote yet, show drop area or ITAR contact form */}
                                    {!quote &&
                                        (itarUploadAnswer === true ? (
                                            <ITARContactSalesForm onSubmit={handleITARContactFormSubmit} />
                                        ) : (
                                            <div
                                                data-testid="quote-empty-upload"
                                                className="add-part-files-container"
                                                onClickCapture={handleBlockedITARUploadIntent}
                                            >
                                                <UploadMultipleFiles
                                                    disabled={!itarUploadAllowed}
                                                    isUploading={
                                                        QuoteStore.fileParts.find(filePart => filePart.uploading)
                                                            ? true
                                                            : false
                                                    }
                                                    hasItems={false}
                                                    totalParts={0}
                                                    onUploadFile={part => handleUploadFile(part)}
                                                />
                                            </div>
                                        ))}

                                    {shouldShowUploadPage && (
                                        <div className="mt-4">
                                            <Footer />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}

                        <CustomModal
                            open={openModal}
                            onCancel={() => {
                                setOpenModal(false);
                                QuoteStore.cleanAllParts();
                            }}
                            onContinue={() => {
                                if (isSubmittingRef.current) return;
                                isSubmittingRef.current = true;
                                submitParts();
                            }}
                            bodyStyle={{ padding: '20px 24px' }}
                            titleText="Match your 2D and 3D files"
                            subTitle="Drag and Drop the files between columns."
                        >
                            <MatchPartFilesTool
                                parts={QuoteStore?.fileParts}
                                onRemovePart={part => removePart(part)}
                                onRemoveFile={(part, fileType, file) => QuoteStore.removeFile(part, fileType, file)}
                                onSwapFile={payload => {
                                    QuoteStore.swapParts(
                                        payload.fromIndex,
                                        payload.toIndex,
                                        payload.fromType,
                                        payload.toType
                                    );
                                }}
                                onAddFile={payload => QuoteStore.addFile(payload.index, payload.type, payload.file)}
                            />
                        </CustomModal>
                    </motion.div>
                </div>

                {/* Quote Sidebar - Only show when there are parts */}
                {quote?.line_items && quote?.line_items?.length > 0 && (
                    <div
                        className="right-content lg:col-span-1"
                        style={{
                            flex: 1,
                            // Mirror the left column: on desktop the sidebar owns its own scroll
                            // region so the parent's definite `height` doesn't clip the bottom
                            // actions (continue button). On mobile fall back to page scroll.
                            ...(UIStore.isMobileViewport
                                ? { minHeight: undefined, overflowY: 'visible' as const }
                                : { minHeight: 0, height: '100%', overflowY: 'auto' as const }),
                        }}
                    >
                        <QuoteSideBar
                            quote={(quote as unknown) as QuotePropsFromTypes}
                            isStaffUser={UserStore.isStaff || false}
                            onUpdateQuoteField={(field, value) => {
                                const payload = { [field]: value };
                                handleUpdateQuoteFields(payload);
                            }}
                            onUpdateQuoteFields={handleUpdateQuoteFields}
                            loading={QuoteStore.quoteSummaryLoading}
                            onRequestQuote={(payload: any, quote_uid?: string) => {
                                if (quote_uid) {
                                    QuoteStore.setRapidQuoteUid(quote_uid);
                                }
                                handleSubmitRFQ(payload);
                            }}
                            isUserLoggedIn={UserStore.isAuthenticated}
                            additionalRequirements={AdditionalRequirementStore.additionalRequirements}
                            onSaveDraft={() => {
                                history.push('/my-account/quotes');
                            }}
                            onGetHelp={() => {
                                history.push('/my-account/support');
                            }}
                            zipCode={zipCode}
                            estimatedTax={UserStore.estimatedTax}
                            areAllItemsSafeToAutoQuote={QuoteStore.areAllItemsSafeToAutoQuote}
                            hasPendingCustomOption={hasPendingCustomOption}
                            sortedMasterProducts={
                                (QuoteStore.sortedMasterProducts as unknown) as QuoteLineItemPropsFromTypes[][]
                            }
                            onRequestModalLoggedIn={setRequestModalLoggedIn}
                        />
                    </div>
                )}
            </>
            <BackgroundUploadTray />
            {authModalVisible && (
                <AuthModal
                    open={authModalVisible}
                    loading={loading}
                    formType={authModalForm}
                    onCancel={() => {
                        setAuthModalVisible(false);
                    }}
                    onChangeFormType={setAuthModalForm}
                    emailForm={
                        <RegisterForm
                            form={loginForm}
                            loading={loading}
                            emailDomains={UserStore.emailDomains}
                            onSubmit={handleRapidQuoteSubmit}
                            onSwitchToLogin={() => setAuthModalForm('login')}
                        />
                    }
                    loginForm={
                        <LoginForm
                            form={loginForm}
                            loading={loading}
                            onSubmit={submitRapidQuoteLogin}
                            onGoBack={() => setAuthModalForm('register')}
                            onForgotPassword={() => history.push('/reset-password/')}
                        />
                    }
                />
            )}
            {requestModalLoggedIn ? (
                <LoginOrRegisterModal
                    open={requestModalLoggedIn}
                    onClose={() => setRequestModalLoggedIn(false)}
                    first_name={quote?.first_name || ''}
                    last_name={quote?.last_name || ''}
                    email={quote?.email || ''}
                    company={quote?.company || ''}
                    onResendVerification={() => {
                        UserStore.resend();
                    }}
                    onSuccess={(quoteId?: string) => {
                        setRequestModalLoggedIn(false);
                        if (quoteId) {
                            QuoteStore.setQuoteUid(quoteId);
                        }
                        handleSubmitRFQ({ markAsCompleted: false });
                    }}
                />
            ) : null}

            <Feedback
                isQuotes={true}
                visible={showFeedbackModal}
                setVisible={setShowFeedbackModal}
                redirectToThisPage={() => redirectToQuotes()}
            />

            {quote && quote?.uid && (
                <>
                    <SocketConnection
                        quoteToken={quote?.uid}
                        setLineItem={item => {
                            // onLineItemUpdated(item as any)
                        }}
                        setConnectedSocket={status => QuoteStore.setSocketStatus(status)}
                    />
                </>
            )}

            <EmbeddedAttachmentsModal
                visible={QuoteStore.pendingEmbeddedExtractionQueueSize > 0}
                attachments={QuoteStore.pendingEmbeddedExtraction?.attachments ?? []}
                loading={QuoteStore.extractEmbeddedLoading}
                queuePosition={1}
                queueTotal={QuoteStore.pendingEmbeddedExtractionQueueSize}
                partLabel={QuoteStore.pendingEmbeddedExtraction?.partLabel}
                onExtract={filenames => QuoteStore.confirmExtractEmbedded3d(filenames)}
                onSkip={() => QuoteStore.clearPendingEmbeddedExtraction()}
            />
        </div>
    );
};

export default observer(WelcomeToGetQuote);
